import { useCallback, useState } from 'react';

import { ScreeningQuestion } from '@/interface';

import { DEFAULT_SEGMENT, SCREENING_QUESTION } from '@/enum/screening-question-type.enum';

export const DEFAULT_LIKERT_OPTIONS = ['1', '2', '3', '4', '5'];

export const DEFAULT_LIKERT_STATEMENTS = ['Statement 1'];

interface ScreeningQuestionValidationTarget {
  message: string;
  questionIndex: number;
  field: 'question' | 'option' | 'statement';
  itemIndex?: number;
}

const createEmptyScreeningQuestion = (segment?: string): ScreeningQuestion => ({
  question: '',
  type: SCREENING_QUESTION.SHORT_TEXT,
  description: '',
  options: [],
  statements: [],
  required: false,
  segment,
});

const cloneScreeningQuestions = (screeningQuestions: ScreeningQuestion[] = []): ScreeningQuestion[] =>
  screeningQuestions.map((question) => ({
    question: question.question || '',
    type: question.type || SCREENING_QUESTION.SHORT_TEXT,
    description: question.description || '',
    options: question.options ? [...question.options] : [],
    statements: question.statements ? [...question.statements] : [],
    required: question.required ?? false,
    allowOthers: question.type === SCREENING_QUESTION.MULTI_CHOICE ? (question.allowOthers ?? false) : undefined,
    segment: question.segment || undefined,
  }));

const getScreeningQuestionsValidationError = (questions: ScreeningQuestion[]): ScreeningQuestionValidationTarget | null => {
  const emptyQuestionIndex = questions.findIndex((question) => !question.question.trim());
  if (emptyQuestionIndex !== -1) {
    return {
      message: 'Every question must include text.',
      questionIndex: emptyQuestionIndex,
      field: 'question',
    };
  }

  const multiChoiceWithoutOptionsIndex = questions.findIndex(
    (question) => question.type === SCREENING_QUESTION.MULTI_CHOICE && !question.options?.some((option) => option.trim()),
  );
  if (multiChoiceWithoutOptionsIndex !== -1) {
    return {
      message: 'Multiple choice questions must include at least one option.',
      questionIndex: multiChoiceWithoutOptionsIndex,
      field: 'option',
    };
  }

  const emptyMultiChoiceOptionIndex = questions.findIndex(
    (question) => question.type === SCREENING_QUESTION.MULTI_CHOICE && (question.options || []).some((option) => !option.trim()),
  );
  if (emptyMultiChoiceOptionIndex !== -1) {
    const itemIndex = questions[emptyMultiChoiceOptionIndex].options?.findIndex((option) => !option.trim());
    return {
      message: 'Please fill every multiple choice option or delete empty options.',
      questionIndex: emptyMultiChoiceOptionIndex,
      field: 'option',
      itemIndex: itemIndex === -1 ? undefined : itemIndex,
    };
  }

  const likertWithoutOptionsOrStatementsIndex = questions.findIndex(
    (question) =>
      question.type === SCREENING_QUESTION.LIKERT_SCALE &&
      (!question.options?.some((option) => option.trim()) || !question.statements?.some((statement) => statement.trim())),
  );
  if (likertWithoutOptionsOrStatementsIndex !== -1) {
    const question = questions[likertWithoutOptionsOrStatementsIndex];
    return {
      message: 'Likert questions must include at least one option and one statement.',
      questionIndex: likertWithoutOptionsOrStatementsIndex,
      field: question.options?.some((option) => option.trim()) ? 'statement' : 'option',
    };
  }

  const emptyLikertOptionIndex = questions.findIndex(
    (question) => question.type === SCREENING_QUESTION.LIKERT_SCALE && (question.options || []).some((option) => !option.trim()),
  );
  if (emptyLikertOptionIndex !== -1) {
    const itemIndex = questions[emptyLikertOptionIndex].options?.findIndex((option) => !option.trim());
    return {
      message: 'Please fill every Likert option or delete empty options.',
      questionIndex: emptyLikertOptionIndex,
      field: 'option',
      itemIndex: itemIndex === -1 ? undefined : itemIndex,
    };
  }

  const emptyLikertStatementIndex = questions.findIndex(
    (question) =>
      question.type === SCREENING_QUESTION.LIKERT_SCALE && (question.statements || []).some((statement) => !statement.trim()),
  );
  if (emptyLikertStatementIndex !== -1) {
    const itemIndex = questions[emptyLikertStatementIndex].statements?.findIndex((statement) => !statement.trim());
    return {
      message: 'Please fill every Likert statement or delete empty statements.',
      questionIndex: emptyLikertStatementIndex,
      field: 'statement',
      itemIndex: itemIndex === -1 ? undefined : itemIndex,
    };
  }

  return null;
};

const normalizeScreeningQuestions = (questions: ScreeningQuestion[]): ScreeningQuestion[] =>
  questions
    .map((question) => ({
      question: question.question.trim(),
      type: question.type,
      description:
        question.type === SCREENING_QUESTION.LIKERT_SCALE && question.description?.trim()
          ? question.description.trim()
          : undefined,
      required: question.required,
      allowOthers: question.type === SCREENING_QUESTION.MULTI_CHOICE ? (question.allowOthers ?? false) : undefined,
      segment: question.segment?.trim() || DEFAULT_SEGMENT,
      options:
        question.type === SCREENING_QUESTION.MULTI_CHOICE || question.type === SCREENING_QUESTION.LIKERT_SCALE
          ? (question.options || []).map((option) => option.trim()).filter(Boolean)
          : undefined,
      statements:
        question.type === SCREENING_QUESTION.LIKERT_SCALE
          ? (question.statements || []).map((statement) => statement.trim()).filter(Boolean)
          : undefined,
    }))
    .filter((question) => question.question);

export const useScreeningQuestionsForm = (initialQuestions: ScreeningQuestion[] = []) => {
  const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestion[]>(cloneScreeningQuestions(initialQuestions));
  const [questionValidationError, setQuestionValidationError] = useState<string | null>(null);

  const resetScreeningQuestions = useCallback((questions: ScreeningQuestion[] = []) => {
    setScreeningQuestions(cloneScreeningQuestions(questions));
    setQuestionValidationError(null);
  }, []);

  const addScreeningQuestion = useCallback((segment?: string) => {
    setScreeningQuestions((prev) => [...prev, createEmptyScreeningQuestion(segment)]);
  }, []);

  const updateScreeningQuestion = useCallback((index: number, updated: Partial<ScreeningQuestion>) => {
    setScreeningQuestions((prev) => prev.map((question, idx) => (idx === index ? { ...question, ...updated } : question)));
  }, []);

  const removeScreeningQuestion = useCallback((index: number) => {
    setScreeningQuestions((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const addQuestionOption = useCallback((index: number) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => (idx === index ? { ...question, options: [...(question.options || []), ''] } : question)),
    );
  }, []);

  const updateQuestionOption = useCallback((questionIndex: number, optionIndex: number, value: string) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        const nextOptions = [...(question.options || [])];
        nextOptions[optionIndex] = value;
        return { ...question, options: nextOptions };
      }),
    );
  }, []);

  const removeQuestionOption = useCallback((questionIndex: number, optionIndex: number) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        const nextOptions = (question.options || []).filter((_, optIdx) => optIdx !== optionIndex);
        return { ...question, options: nextOptions };
      }),
    );
  }, []);

  const addQuestionStatement = useCallback((index: number) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => (idx === index ? { ...question, statements: [...(question.statements || []), ''] } : question)),
    );
  }, []);

  const updateQuestionStatement = useCallback((questionIndex: number, statementIndex: number, value: string) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        const nextStatements = [...(question.statements || [])];
        nextStatements[statementIndex] = value;
        return { ...question, statements: nextStatements };
      }),
    );
  }, []);

  const removeQuestionStatement = useCallback((questionIndex: number, statementIndex: number) => {
    setScreeningQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        const nextStatements = (question.statements || []).filter((_, stmtIdx) => stmtIdx !== statementIndex);
        return { ...question, statements: nextStatements };
      }),
    );
  }, []);

  const validateScreeningQuestions = useCallback(() => {
    const validationError = getScreeningQuestionsValidationError(screeningQuestions);
    setQuestionValidationError(validationError?.message || null);
    return validationError;
  }, [screeningQuestions]);

  return {
    screeningQuestions,
    setScreeningQuestions,
    questionValidationError,
    resetScreeningQuestions,
    addScreeningQuestion,
    updateScreeningQuestion,
    removeScreeningQuestion,
    addQuestionOption,
    updateQuestionOption,
    removeQuestionOption,
    addQuestionStatement,
    updateQuestionStatement,
    removeQuestionStatement,
    validateScreeningQuestions,
    normalizeScreeningQuestions: useCallback(() => normalizeScreeningQuestions(screeningQuestions), [screeningQuestions]),
  };
};
